import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import { useGetParentPaymentsReceivedQuery } from "../../../store/api/school/PaymentHistorySlice";
import ParentPaymentDetailModal from "./ParentPaymentDetailModal";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";
import { Button } from "react-bootstrap";
import Tooltip from "../../../component/Tooltip";

const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (status) => {
  if (!status) return "status unavailable";
  const s = status.toLowerCase();
  if (s === "completed" || s === "paid") return "status bg-success text-white";
  if (s === "cancelled" || s === "rejected")
    return "status bg-danger text-white";
  if (s === "partial") return "status bg-warning text-dark";
  if (s === "pending" || s === "created") return "status unavailable";
  return "status unavailable";
};

const paymentTypeLabel = (type) => {
  if (type === "full_payment") return "Full";
  if (type === "partial_payment") return "Partial";
  if (type === "student_fee") return "Student Fee";
  return type || "—";
};

const EffectiveBankCell = ({ effectiveBank }) => {
  if (!effectiveBank) return <span className="text-muted">—</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span>{effectiveBank.bank_name || "—"}</span>
      {effectiveBank.routed_through_admin && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 10,
            background: "#fde8c8",
            color: "#c8900a",
            border: "1px solid #f0b342",
            width: "fit-content",
          }}
        >
          via Admin
        </span>
      )}
    </div>
  );
};

const EXTERNAL_METHOD_LABELS = {
  bank_wire: "Bank Wire",
  cash: "Cash",
  cheque: "Cheque",
  other: "Other",
};

// Shows how a payment was actually made — external (bank wire/cash/cheque)
// vs. the bank routed through Omni — instead of just a bank name, so it's
// clear at a glance which payments never touched the payment gateway.
const PaymentMethodCell = ({ item }) => {
  if (item.payment_source === "external") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 10,
            background: "#dbeafe",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
            width: "fit-content",
          }}
        >
          External
        </span>
        <span style={{ fontSize: 12 }}>
          {EXTERNAL_METHOD_LABELS[item.external_payment_method] ||
            item.external_payment_method ||
            "—"}
        </span>
      </div>
    );
  }
  return <EffectiveBankCell effectiveBank={item.effective_bank} />;
};

const ParentPaymentsReceived = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedSummaryId, setSelectedSummaryId] = useState(null);

  const { data, isSuccess, isLoading, isFetching } =
    useGetParentPaymentsReceivedQuery({
      page: pagination.page,
      limit: pagination.limit,
    });

  useEffect(() => {
    if (isSuccess && data?.data) {
      setHistory(data.data.history || []);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) =>
    setPagination((prev) => ({ ...prev, limit, page: 1 }));

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
        <div className="dbSubHd">
          {t("total")}: {pagination.total}
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">{t("parent")}</th>
              <th className="text-nowrap">{t("orderNo")}</th>
              <th className="text-nowrap">{t("type")}</th>
              <th className="text-nowrap">{t("amount")}</th>
              <th className="text-nowrap">{t("bankUsed")}</th>
              <th className="text-nowrap">{t("date")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("status")}
              </th>
              <th className="text-nowrap">{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={9} />
            ) : history?.length > 0 ? (
              history.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>

                  {/* Parent (Payer) */}
                  <td className="text-nowrap">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <strong>{item.payer?.name || "—"}</strong>
                      <small style={{ fontSize: 11, color: "#94a3b8" }}>
                        {item.payer?.email}
                      </small>
                    </div>
                  </td>

                  <td className="text-nowrap">
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        background: "#f0f3f6",
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}
                    >
                      {item.order_number || "—"}
                    </span>
                  </td>

                  <td className="text-nowrap">
                    {paymentTypeLabel(item.payment_type)}
                  </td>

                  <td className="text-nowrap">
                    <strong>
                      {item.currency === "CAD" ? "C$" : "$"}
                      {(item.amount_cents / 100).toFixed(2)}
                    </strong>
                    <small className="ms-1 text-muted">{item.currency}</small>
                  </td>

                  {/* Bank Used / External Method */}
                  <td className="text-nowrap">
                    <PaymentMethodCell item={item} />
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>
                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {item.payment_status &&
                        capitalizeFirstLetter(item.payment_status)}
                    </span>
                  </td>

                  <td>
                    <div className="actionBtn d-flex">
                      {item.payment_summary_id && (
                        <Tooltip title={`${t("payment")} ${t("details")}`}>
                          <Button
                            className="rounded-circle iconBtn me-2"
                            onClick={() =>
                              setSelectedSummaryId(item.payment_summary_id)
                            }
                          >
                            <svg
                              id="Group_3073"
                              data-name="Group 3073"
                              xmlns="http://www.w3.org/2000/svg"
                              width="22.592"
                              height="22.592"
                              viewBox="0 0 22.592 22.592"
                            >
                              <path
                                id="calendar-check"
                                d="M17.886,1.883h-.941V.941a.941.941,0,0,0-1.883,0v.941H7.531V.941a.941.941,0,1,0-1.883,0v.941H4.707A4.712,4.712,0,0,0,0,6.589v11.3a4.712,4.712,0,0,0,4.707,4.707H17.886a4.712,4.712,0,0,0,4.707-4.707V6.589A4.712,4.712,0,0,0,17.886,1.883ZM4.707,3.765H17.886A2.827,2.827,0,0,1,20.71,6.589v.941H1.883V6.589A2.827,2.827,0,0,1,4.707,3.765ZM17.886,20.71H4.707a2.827,2.827,0,0,1-2.824-2.824V9.413H20.71v8.472A2.827,2.827,0,0,1,17.886,20.71Z"
                                fill="#727782"
                              />
                              <g
                                id="refresh"
                                transform="translate(6.776 10.498)"
                              >
                                <path
                                  id="Path_1637"
                                  data-name="Path 1637"
                                  d="M3.591,4.063a3.425,3.425,0,0,1,5.615-2.1L8.764,2.4a.379.379,0,0,0,.268.647H10.77a.379.379,0,0,0,.379-.379V.931A.379.379,0,0,0,10.5.663l-.493.493A4.557,4.557,0,0,0,2.469,3.9a.567.567,0,1,0,1.122.166Z"
                                  transform="translate(-2.461 0)"
                                  fill="#727782"
                                />
                                <path
                                  id="Path_1638"
                                  data-name="Path 1638"
                                  d="M30.356,256.891a.574.574,0,0,0-.563.5,3.425,3.425,0,0,1-5.615,2.1l.441-.441a.379.379,0,0,0-.268-.647H22.614a.379.379,0,0,0-.379.379v1.738a.379.379,0,0,0,.647.268l.493-.493a4.556,4.556,0,0,0,7.541-2.742.574.574,0,0,0-.474-.659A.555.555,0,0,0,30.356,256.891Z"
                                  transform="translate(-21.884 -252.328)"
                                  fill="#727782"
                                />
                              </g>
                            </svg>
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="9">
                  {t("no")} {t("details")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <ParentPaymentDetailModal
        summaryId={selectedSummaryId}
        onClose={() => setSelectedSummaryId(null)}
      />
    </>
  );
};

export default ParentPaymentsReceived;
